import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Store, ArrowLeft, BadgeCheck, MapPin, Share2, Clock, Search, Truck, RotateCcw, Tag, ShieldCheck, Mail, Phone, Star } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import ShopHeader from "@/components/ShopHeader";
import { toast } from "sonner";

const setMeta = (name, content, prop = false) => {
  const attr = prop ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
};
const areaSummary = (a) => {
  if (!a) return null;
  if (a.all_india) return "All India";
  return [a.states?.length && `${a.states.join(", ")}`, a.districts?.length && `${a.districts.join(", ")}`, a.pincodes?.length && `${a.pincodes.length} pincode(s)`].filter(Boolean).join(" · ") || null;
};
const offerLabel = (o) => o.type === "percentage" ? `${o.value}% off` : o.type === "fixed" ? `₹${o.value} off` : o.type === "free_shipping" ? "Free shipping" : (o.label || "Offer");

export default function Storefront() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, sf: null });
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const [page, setPage] = useState(1);

  const fetchStore = useCallback((opts = {}) => {
    const params = { q: opts.q ?? q, category: opts.category ?? activeCat, page: opts.page ?? page };
    api.get(`/marketplace/public/storefront/${slug}`, { params })
      .then((r) => setState({ loading: false, sf: r.data.storefront }))
      .catch(() => setState({ loading: false, sf: null }));
  }, [slug, q, activeCat, page]);

  const ev = useCallback((type, product_id) => {
    api.post(`/marketplace/public/storefront/${slug}/event`, { type, product_id }).catch(() => {});
  }, [slug]);

  useEffect(() => { fetchStore({ q: "", category: "", page: 1 }); ev("view"); /* eslint-disable-next-line */ }, [slug]);

  const sf = state.sf;
  const accent = (sf && sf.accent) || "#4f46e5";
  const sec = (sf && sf.sections) || {};
  const show = (k) => sec[k] !== false;

  useEffect(() => {
    if (!sf) return;
    document.title = sf.seo_title || `${sf.name} — Suffle Online Marketplace`;
    setMeta("description", (sf.seo_description || sf.about || `Shop from ${sf.name} on Suffle Online.`).slice(0, 160));
    setMeta("og:title", sf.seo_title || `${sf.name} — Suffle Online`, true);
    setMeta("og:description", (sf.seo_description || sf.about || `${sf.name} on Suffle Online`).slice(0, 160), true);
    setMeta("og:type", "website", true);
    if (sf.logo) setMeta("og:image", sf.logo, true);
    setMeta("robots", sf.active ? "index,follow" : "noindex,nofollow");
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = `${window.location.origin}/${sf.slug}`;
    return () => { document.title = "Suffle Online"; setMeta("robots", "index,follow"); };
  }, [sf]);

  const share = async () => {
    const url = `${window.location.origin}/${slug}`;
    try { if (navigator.share) await navigator.share({ title: sf?.name, url }); else { await navigator.clipboard.writeText(url); toast.success("Store link copied"); } }
    catch { /* cancelled */ }
  };
  const runSearch = () => { setPage(1); ev("search"); fetchStore({ page: 1 }); };
  const pickCat = (c) => { const nc = activeCat === c ? "" : c; setActiveCat(nc); setPage(1); if (nc) ev("category_click"); fetchStore({ category: nc, page: 1 }); };
  const goPage = (p) => { setPage(p); fetchStore({ page: p }); window.scrollTo({ top: 400, behavior: "smooth" }); };

  if (state.loading) return <div className="flex min-h-screen items-center justify-center bg-[#0b0a1f]" data-testid="storefront-loading"><Logo variant="lg" /></div>;

  if (!sf) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center" data-testid="storefront-not-found">
        <Store className="h-10 w-10 text-slate-300" />
        <h1 className="text-xl font-bold text-slate-800">Storefront not found</h1>
        <p className="max-w-sm text-sm text-slate-500">We couldn't find a seller storefront at <span className="font-mono">/{slug}</span>.</p>
        <Link to="/" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white"><ArrowLeft className="h-4 w-4" /> Back to Suffle Online</Link>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil((sf.total_products || 0) / 12));
  const ProductGrid = ({ list }) => (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {list.map((p) => (
        <div key={p.product_id} onClick={() => { ev("product_click", p.product_id); nav(`/product/${p.slug || p.product_id}`); }} data-testid={`store-product-${p.product_id}`} className="cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:shadow-md">
          <div className="relative aspect-square bg-slate-100">
            {p.image ? <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Store className="h-8 w-8 text-slate-300" /></div>}
            {p.discount > 0 && <span className="absolute left-2 top-2 rounded-md px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: accent }}>{p.discount}% OFF</span>}
          </div>
          <div className="p-2.5">
            {p.brand && <p className="truncate text-[10px] uppercase tracking-wide text-slate-400">{p.brand}</p>}
            <p className="line-clamp-2 text-sm font-medium text-slate-800">{p.name}</p>
            <div className="mt-1 flex items-baseline gap-1"><span className="font-bold text-slate-900">₹{p.price}</span>{p.discount > 0 && <span className="text-xs text-slate-400 line-through">₹{p.mrp}</span>}</div>
            {p.return_policy && (p.return_policy.non_returnable
              ? <span className="mt-1 inline-block rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600" data-testid={`store-return-${p.product_id}`}>Non-returnable</span>
              : (p.return_policy.window_days > 0 && <span className="mt-1 inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`store-return-${p.product_id}`}>{p.return_policy.window_days}-day returns</span>))}
            {!p.in_stock && <p className="mt-1 text-[11px] font-semibold text-rose-500">Out of stock</p>}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="storefront-page">
      <ShopHeader />

      {sf.announcement && <div className="px-4 py-2 text-center text-xs font-medium text-white" style={{ backgroundColor: accent }} data-testid="store-announcement">{sf.announcement}</div>}

      {/* Hero banner */}
      <div className="relative h-40 w-full overflow-hidden sm:h-56" data-testid="store-banner" style={sf.banner ? undefined : { backgroundColor: "#0b0a1f", backgroundImage: `radial-gradient(70% 60% at 15% 0%, ${accent}88, transparent 60%), radial-gradient(70% 70% at 100% 30%, rgba(219,39,119,.35), transparent 60%)` }}>
        {sf.banner && (<>
          <div className="absolute inset-0 scale-110 blur-2xl" style={{ backgroundImage: `url(${sf.banner})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="absolute inset-0" style={{ backgroundImage: `url(${sf.banner})`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }} />
        </>)}
      </div>

      <main className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Seller header */}
        <div className="flex flex-wrap items-start gap-4">
          <div className="-mt-12 flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow ring-1 ring-slate-200">
            {sf.logo ? <img src={sf.logo} alt="" className="h-full w-full rounded-2xl object-cover" /> : <Store className="h-8 w-8 text-slate-400" />}
          </div>
          <div className="pt-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900" data-testid="storefront-name">{sf.name}</h1>
              {sf.verified && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid="verified-badge"><BadgeCheck className="h-3.5 w-3.5" /> Verified Seller</span>}
            </div>
            {sf.tagline && <p className="mt-0.5 text-sm text-slate-600" data-testid="store-tagline">{sf.tagline}</p>}
            {sf.location && <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {sf.location}</p>}
          </div>
        </div>

        {!sf.active && (
          <div className="mt-6 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700" data-testid="storefront-inactive">
            <Clock className="h-4 w-4" /> {sf.store_status === "unavailable" ? "This store is temporarily unavailable and isn't taking orders right now." : "This store isn't active on the marketplace yet. It can't take orders right now."}
          </div>
        )}

        {/* Store search */}
        {sf.active && (
          <div className="mt-6 flex gap-2" data-testid="store-search">
            <div className="relative flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder={`Search ${sf.name}…`} data-testid="store-search-input" className="w-full rounded-full border border-slate-200 py-2 pl-9 pr-4 text-sm" /></div>
            <button onClick={runSearch} data-testid="store-search-btn" className="rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ backgroundColor: accent }}>Search</button>
          </div>
        )}

        {/* Categories */}
        {sf.active && show("categories") && (sf.categories || []).length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="store-categories">
            {sf.categories.map((c) => (
              <button key={c.category_id} onClick={() => pickCat(c.category_id)} data-testid={`store-cat-${c.category_id}`}
                className={`rounded-full px-3 py-1.5 text-xs font-medium ${activeCat === c.category_id ? "text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}
                style={activeCat === c.category_id ? { backgroundColor: accent } : {}}>{c.name}</button>
            ))}
          </div>
        )}

        {/* Offers */}
        {sf.active && show("offers") && (sf.offers || []).length > 0 && (
          <section className="mt-6" data-testid="store-offers">
            <h2 className="mb-2 text-sm font-bold text-slate-800">Offers</h2>
            <div className="flex flex-wrap gap-2">
              {sf.offers.map((o, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed px-3 py-1.5 text-xs font-semibold" style={{ borderColor: accent, color: accent }}>
                  <Tag className="h-3.5 w-3.5" /> {offerLabel(o)}{o.code ? ` · ${o.code}` : ""}
                  {o.source && <em className="not-italic rounded px-1.5 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: o.source === "store" ? `${accent}22` : "#e2e8f0", color: o.source === "store" ? accent : "#475569" }}>{o.source === "store" ? "Store offer" : "Suffle offer"}</em>}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Featured */}
        {sf.active && show("featured") && (sf.featured || []).length > 0 && (
          <section className="mt-8" data-testid="store-featured">
            <h2 className="mb-3 text-sm font-bold text-slate-800">Featured products</h2>
            <ProductGrid list={sf.featured} />
          </section>
        )}

        {/* All products */}
        {sf.active && (
          <section className="mt-8" data-testid="store-products">
            <h2 className="mb-3 text-sm font-bold text-slate-800">{activeCat || q ? "Results" : "All products"}</h2>
            {(sf.products || []).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="store-products-empty">
                <Store className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 font-semibold text-slate-700">No products to show</p>
                <p className="mt-1 text-sm text-slate-500">This seller hasn't published matching products yet. Availability reflects your delivery location.</p>
              </div>
            ) : (
              <>
                <ProductGrid list={sf.products} />
                {totalPages > 1 && (
                  <div className="mt-5 flex items-center justify-center gap-1" data-testid="store-pagination">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button key={p} onClick={() => goPage(p)} className={`h-8 w-8 rounded-lg text-xs font-semibold ${p === page ? "text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`} style={p === page ? { backgroundColor: accent } : {}}>{p}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* About */}
        {show("about") && sf.about && (
          <section className="mt-10" data-testid="store-about">
            <h2 className="mb-2 text-sm font-bold text-slate-800">About the seller</h2>
            <p className="max-w-3xl whitespace-pre-line text-sm text-slate-600">{sf.about}</p>
          </section>
        )}

        {/* Ride ratings feed */}
        {(sf.driver_reviews || []).length > 0 && (
          <section className="mt-10" data-testid="store-ride-ratings">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-sm font-bold text-slate-800">What riders say</h2>
              {sf.driver_rating ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-600" data-testid="store-driver-rating">
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {sf.driver_rating} <span className="font-medium text-slate-400">({sf.driver_rating_count})</span>
                </span>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {sf.driver_reviews.map((r, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`store-ride-review-${i}`}>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}
                  </div>
                  {r.review ? <p className="mt-2 line-clamp-4 text-sm text-slate-600">“{r.review}”</p> : <p className="mt-2 text-sm italic text-slate-400">Rated {r.rating}/5</p>}
                  <p className="mt-2 text-xs font-semibold text-slate-500">{r.driver_name} · <span className="font-normal text-slate-400">{r.service_name}</span></p>
                  {r.provider_reply && (
                    <div className="mt-2 rounded-lg border-l-2 border-sky-300 bg-slate-50 p-2" data-testid={`store-review-reply-${i}`}>
                      <p className="text-[10px] font-semibold text-sky-600">Provider response</p>
                      <p className="text-xs text-slate-600">{r.provider_reply}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Shipping & Delivery */}
        {show("shipping") && (sf.shipping_info || sf.processing_time || areaSummary(sf.selling_area)) && (
          <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5" data-testid="store-shipping">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800"><Truck className="h-4 w-4" style={{ color: accent }} /> Shipping &amp; Delivery</h2>
            {sf.processing_time && <p className="text-sm text-slate-600"><b>Processing time:</b> {sf.processing_time}</p>}
            {areaSummary(sf.selling_area) && <p className="text-sm text-slate-600"><b>Delivers to:</b> {areaSummary(sf.selling_area)}</p>}
            {sf.shipping_info && <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{sf.shipping_info}</p>}
            <p className="mt-2 text-xs text-slate-400">The seller is responsible for packing and courier dispatch. Suffle Online provides the marketplace platform and does not physically operate the seller's courier.</p>
          </section>
        )}

        {/* Returns & Replacements */}
        {show("returns") && (sf.return_info || sf.return_window_days != null) && (
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="store-returns">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800"><RotateCcw className="h-4 w-4" style={{ color: accent }} /> Returns &amp; Replacements</h2>
            {sf.return_window_days != null && <p className="text-sm text-slate-600"><b>Return window:</b> {sf.return_window_days} day(s)</p>}
            {sf.return_info && <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{sf.return_info}</p>}
            <p className="mt-2 text-xs text-slate-400">Seller return information is subordinate to Suffle's central <Link to="/legal/refund-cancellation" className="underline">Return, Replacement &amp; Refund Policy</Link>. Returns are processed through Suffle's central system.</p>
          </section>
        )}

        {/* Seller policies */}
        {show("policies") && sf.policies && (
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="store-policies">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-800"><ShieldCheck className="h-4 w-4" style={{ color: accent }} /> Seller policies</h2>
            <p className="whitespace-pre-line text-sm text-slate-600">{sf.policies}</p>
          </section>
        )}

        {/* Contact */}
        {show("contact") && (sf.support_email || sf.support_phone || sf.hours) && (
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="store-contact">
            <h2 className="mb-2 text-sm font-bold text-slate-800">Contact &amp; support</h2>
            <div className="flex flex-col gap-1 text-sm text-slate-600">
              {sf.support_email && <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> <a href={`mailto:${sf.support_email}`} className="underline">{sf.support_email}</a></span>}
              {sf.support_phone && <span className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {sf.support_phone}</span>}
              {sf.hours && <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {sf.hours}</span>}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-slate-200 bg-white" data-testid="store-footer">
        <div className="mx-auto grid max-w-6xl gap-6 px-6 py-8 sm:grid-cols-3">
          <div>
            <Logo variant="sm" />
            <p className="mt-2 text-xs text-slate-500">{sf.name} is an approved seller on <b>Suffle Online</b> — Shop. Discover. Suffle.</p>
            {sf.location && <p className="mt-1 text-xs text-slate-400">{sf.location}</p>}
          </div>
          <div className="text-sm">
            <p className="mb-2 font-semibold text-slate-700">Store</p>
            <ul className="space-y-1 text-slate-500">
              <li><button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="hover:text-slate-800">Shop</button></li>
              {show("shipping") && <li><a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid=store-shipping]')?.scrollIntoView({ behavior: "smooth" }); }} className="hover:text-slate-800">Shipping</a></li>}
              {show("returns") && <li><a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid=store-returns]')?.scrollIntoView({ behavior: "smooth" }); }} className="hover:text-slate-800">Returns</a></li>}
              {sf.support_email && <li><a href={`mailto:${sf.support_email}`} className="hover:text-slate-800">Contact</a></li>}
            </ul>
          </div>
          <div className="text-sm">
            <p className="mb-2 font-semibold text-slate-700">Marketplace policies</p>
            <ul className="space-y-1 text-slate-500">
              <li><Link to="/legal/terms-conditions" className="hover:text-slate-800">Terms &amp; Conditions</Link></li>
              <li><Link to="/legal/privacy-policy" className="hover:text-slate-800">Privacy Policy</Link></li>
              <li><Link to="/legal/refund-cancellation" className="hover:text-slate-800">Return &amp; Refund Policy</Link></li>
              <li><a href="mailto:support@suffleonline.com" className="hover:text-slate-800">Support</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-100 py-4 text-center text-xs text-slate-400">© {new Date().getFullYear()} Suffle Online · Naucera Travel Private Limited</div>
      </footer>
    </div>
  );
}
